<template>
  <div class="tv-health">
    <ArticleCardLarge
      class="tv-health__large-card"
      v-bind="useArticleProps(firstArticle)"
      :loading-views="getLoadingViews(firstArticle.id)"
      :img-lazy="false"
      :show-category-icon="false"
      :is-authenticated-in-wordpress="isAuthenticatedInWordpress"
      @clicked-on-comments="sendEvent"
      @click-on-restore-button="updateViewsCount(firstArticle.id)"
      @favorite-delete="(favorite) => onFavoriteDelete(favorite, firstArticle)"
      @favorite-create-folder-and-add="
        (nameFolder) => onFavoriteCreateFolderAndAdd(nameFolder, firstArticle)
      "
      @favorite-create-folder-and-transfer="
        (nameFolder, favorite) =>
          onFavoriteCreateFolderAndTransfer(nameFolder, favorite, firstArticle)
      "
      @favorite-transfer-to-folder="
        (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, firstArticle)
      "
      @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, firstArticle)"
      @favorite-deferred="onFavoriteDeferred"
      @clicked-on-category="onClickCategory"
      @click-on-article-card="onClickArticleCardLarge"
    />
    <div class="tv-health__list">
      <div
        v-for="article in otherArticles"
        :key="article.id"
        class="tv-health__list-item-wrapper"
      >
        <ArticleCardSmall
          class="tv-health__list-item"
          v-bind="useArticleProps(article)"
          :loading-views="getLoadingViews(article.id)"
          @appear="onAppear(article)"
          @clicked-on-category="onClickCategory"
          @click-on-restore-button="updateViewsCount(article.id)"
          @favorite-delete="
            (favorite) => {
              onFavoriteDelete(favorite, article, false);
            }
          "
          @favorite-create-folder-and-add="
            (nameFolder) => {
              onFavoriteCreateFolderAndAdd(nameFolder, article, false);
            }
          "
          @favorite-create-folder-and-transfer="
            (nameFolder, favorite) => {
              onFavoriteCreateFolderAndTransfer(nameFolder, favorite, article, false);
            }
          "
          @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, article)"
          @favorite-deferred="onFavoriteDeferred"
          @favorite-transfer-to-folder="
            (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article, update)
          "
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { ArticleCardLarge, ArticleCardSmall } from '@/components/lh-ui/ArticleCard';

  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { getFullUrl } from '~/utils';
  import { useIndexStore } from '~/store';
  import { useArticleProps } from '~/composables/useArticleProps';
  import { useFavorite } from '@/composables/useFavoriteMethods';
  import { useUpdatingViewsCount } from '@/composables/useUpdatingViewsCount';

  export default defineNuxtComponent({
    name: 'TheTvHealth',

    components: {
      ArticleCardLarge,
      ArticleCardSmall,
    },

    props: {
      articlesItems: {
        type: Array as PropType<Array<IArticle>>,
        required: true,
      },
    },

    setup() {
      const {
        onFavoriteDelete,
        onFavoriteCreateFolderAndAdd,
        onFavoriteCreateFolderAndTransfer,
        onFavoriteTransferToFolder,
        onFavoriteAddToFolder,
        onFavoriteDeferred,
      } = useFavorite();
      const { loadingViewsIds, updateViewsCount, getLoadingViews } = useUpdatingViewsCount();

      return {
        onFavoriteDelete,
        onFavoriteCreateFolderAndAdd,
        onFavoriteCreateFolderAndTransfer,
        onFavoriteTransferToFolder,
        onFavoriteAddToFolder,
        onFavoriteDeferred,
        indexStore: useIndexStore(),
        loadingViewsIds,
        updateViewsCount,
        getLoadingViews,
      };
    },

    computed: {
      firstArticle(): IArticle {
        return this.articlesItems[0];
      },
      otherArticles(): Array<IArticle> {
        return this.articlesItems.slice(1, 5);
      },
      isAuthenticatedInWordpress(): boolean {
        return this.indexStore.isAuthenticatedInWordpress;
      },
    },

    methods: {
      useArticleProps,
      sendEvent(_article: IArticle) {},
      onClickCategory(link: string, titleCategory: string, title: string) {
        this.$sendYandexMetrika({
          level1: 'Выбор рубрики_телевизор здоровье',
          level4: getSlugFromUrl(link),
          level5: titleCategory,
          level6: title,
        });
      },
      onClickArticleCardLarge() {
        this.$sendYandexMetrika({
          level1: 'Выбор материала_Виджет большая карточка',
          level4: getFullUrl(this.firstArticle?.url) || '',
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  $padding: 16px;
  $paddingTablet: 24px;

  .tv-health {
    border: none;

    @include tablet {
      border-top: 1px solid $borderColor;
    }

    // Перебивает глобальный стиль для large-article-card__wrapper
    &__large-card.lh-large-article-card__wrapper {
      margin-top: 0;
      border-bottom: 1px solid $borderColor;
    }

    :deep() {
      .tv-health__list-item {
        position: relative;

        @include hover {
          z-index: 4;
        }
      }

      .tv-health__list-item-wrapper:not(:last-child) {
        .tv-health__list-item::after {
          position: absolute;
          left: $padding;
          bottom: 0;

          height: 1px;
          width: calc(100% - (#{$padding * 2}));

          background: #e7e7e7;
          content: '';

          @include tablet {
            left: $paddingTablet;

            width: calc(100% - (#{$paddingTablet} * 2));
          }
        }
      }
    }
  }
</style>
